import { Helmet } from "react-helmet-async";
import { Phone, MessageCircle, ArrowLeft, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const WHATSAPP_URL = "https://wa.me/919891315777?text=Hi%2C%20I%20want%20to%20book%20a%20water%20tank%20cleaning%20service%20in%20Gurugram";

const serviceLinks = [
  { href: "/residential-tank-cleaning-gurugram", label: "Residential Tank Cleaning" },
  { href: "/overhead-water-tank-cleaning-gurugram", label: "Overhead Tank Cleaning" },
  { href: "/underground-water-tank-cleaning-gurugram", label: "Underground Tank Cleaning" },
  { href: "/commercial-water-tank-cleaning-gurugram", label: "Commercial Tank Cleaning" },
];

const nearbyAreas = [
  { href: "/water-tank-cleaning-dlf-phase-gurugram", label: "DLF Phase 1-5" },
  { href: "/water-tank-cleaning-sushant-lok-gurugram", label: "Sushant Lok" },
  { href: "/water-tank-cleaning-palam-vihar-gurugram", label: "Palam Vihar" },
  { href: "/water-tank-cleaning-golf-course-road-gurugram", label: "Golf Course Road" },
];

export default function Blog1() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>How Often Should You Clean Your Water Tank in Gurugram? | TankBuddy</title>
        <meta name="description" content="Experts recommend cleaning your water tank every 6 months in Gurugram. Find out why and how TankBuddy can help. Call +91 98913 15777" />
        <link rel="canonical" href="https://www.tankbuddy.in/blog/how-often-clean-water-tank-gurugram" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Header />

      <main className="pt-20 pb-16">
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-12">

          <a href="/" className="inline-flex items-center gap-2 text-sky text-sm font-medium mb-8 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </a>

          <div className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full mb-6">
            📝 TankBuddy Blog
          </div>

          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-6">
            How Often Should You Clean Your Water Tank in Gurugram?
          </h1>

          <div className="prose prose-slate max-w-none mb-10">
            <p className="text-slate-600 leading-relaxed text-base">
              Most Gurugram families never think about their water tank until something goes wrong. But your water tank is the last line of defence between your family and contaminated drinking water. Regular cleaning is not optional — it is essential.
            </p>
            <p className="text-slate-600 leading-relaxed text-base mt-4">
              Gurugram's hot summers (45°C+) accelerate bacteria growth inside water tanks by up to 300%. Combined with the city's hard water and dust from construction activity, tanks in DLF, Sushant Lok, Palam Vihar and all Gurugram sectors get dirty faster than most Indian cities.
            </p>
          </div>

          {/* CTA Box */}
          <div className="bg-navy rounded-xl p-8 mb-10 text-white">
            <h2 className="font-heading font-bold text-xl mb-2">Book Tank Cleaning in Gurugram</h2>
            <p className="text-slate-300 text-sm mb-5">Same-day service available. Starting ₹999 for residential tanks.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-whatsapp hover:bg-whatsapp-hover text-white rounded-full px-6">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-4 h-4 mr-2" /> Book on WhatsApp
                </a>
              </Button>
              <Button asChild variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-6">
                <a href="tel:+919891315777">
                  <Phone className="w-4 h-4 mr-2" /> Call +91 98913 15777
                </a>
              </Button>
            </div>
          </div>

          {/* Signs */}
          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">
              Signs Your Tank Needs Cleaning Now
            </h2>
            <ul className="space-y-3">
              {[
                "Water smells different or has a metallic taste",
                "Water looks slightly yellow or brown",
                "Family members getting stomach issues frequently",
                "You haven't cleaned it in over 6 months",
                "Visible algae or slime on tank walls"
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-600">
                  <div className="w-5 h-5 rounded-full bg-sky/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-sky text-xs font-bold">✓</span>
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* What Happens */}
          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">
              What Happens If You Don't Clean?
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Dirty water tanks are the #1 cause of waterborne diseases in Gurugram including typhoid, cholera, and gastroenteritis. The WHO recommends cleaning storage tanks at least twice a year. Children, elderly and pregnant women are most at risk from contaminated tank water.
            </p>
          </div>

          {/* Process */}
          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">
              How TankBuddy Cleans Your Tank
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                {step:"01", title:"Inspection", desc:"We assess tank condition, size and contamination level"},
                {step:"02", title:"Dewatering", desc:"Safe and complete draining of existing water"},
                {step:"03", title:"Jet Cleaning", desc:"High-pressure cleaning of all walls, floor and corners"},
                {step:"04", title:"Disinfection", desc:"Anti-bacterial treatment and UV sanitization"}
              ].map((item) => (
                <div key={item.step} className="bg-surface border border-slate-200 rounded-lg p-5">
                  <span className="text-sky font-bold text-sm">Step {item.step}</span>
                  <h3 className="font-heading font-bold text-navy mt-1 mb-1">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Cost */}
          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">
              Cost of Tank Cleaning in Gurugram
            </h2>
            <p className="text-slate-600 leading-relaxed">
              TankBuddy's residential tank cleaning starts at just ₹999. For a tank cleaned every 6 months, that's less than ₹170 per month — a small price for your family's health and safety.
            </p>
          </div>

          {/* Services */}
          <div className="mb-10">
            <h2 className="font-heading font-bold text-xl text-navy mb-4">Our Services in Gurugram</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {serviceLinks.map(link => (
                <a key={link.href} href={link.href} className="flex items-center gap-3 bg-surface border border-slate-200 rounded-lg p-4 hover:border-sky/50 hover:shadow-sm transition-all">
                  <div className="w-8 h-8 rounded-lg bg-sky/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-sky" />
                  </div>
                  <span className="text-sm font-medium text-navy">{link.label}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Nearby Areas */}
          <div>
            <h2 className="font-heading font-bold text-xl text-navy mb-4">Also Serving Nearby Areas</h2>
            <div className="flex flex-wrap gap-2">
              {nearbyAreas.map(a => (
                <a key={a.href} href={a.href} className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors">
                  <MapPin className="w-3 h-3 text-sky" />{a.label}
                </a>
              ))}
            </div>
          </div>

        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
